import { Box, Stack } from '@ff-it/ui';
import { ReactElement, ReactNode } from 'react';
import type { XLSXThemeConfig } from './type';
import { useFieldValue } from 'hooks';
import { CheckField, IntField, SelectField, TextField } from '@ff-it/form';
import { createOptions } from 'options';
import { composeValidators, required } from 'utilities';

function Preview({ theme }: { theme: XLSXThemeConfig }): ReactElement {
  const borderBottom = theme.bordered ? '1px dotted #000' : undefined;
  return (
    <Stack
      direction="vertical"
      space="0"
      style={{
        fontFamily: theme.font_name,
        border: theme.bordered ? '1px solid #000' : undefined,
        fontSize: `${theme.zoom_scale}%`,
      }}
    >
      <Box
        style={{
          backgroundColor: `#${theme.accent_bg}`,
          color: `#${theme.accent_fg}`,
          borderBottom,
        }}
      >
        Header
      </Box>
      <Box
        style={{
          backgroundColor: `#${theme.h1_bg}`,
          color: `#${theme.h1_fg}`,
          borderBottom,
        }}
      >
        Heading 1
      </Box>
      <Box
        style={{
          backgroundColor: `#${theme.h2_bg}`,
          color: `#${theme.h2_fg}`,
          borderBottom,
        }}
      >
        Heading 2
      </Box>
      <Box
        style={{
          backgroundColor: `#${theme.position_bg}`,
          color: `#${theme.position_fg}`,
          border: '1px solid #000',
        }}
      >
        Position
      </Box>
    </Stack>
  );
}

const fontOptions = createOptions(['Arial', 'Calibri', 'Aptos Narrow', 'Aptos Display']);

const colorProps = {
  parse: (v: string) => v.substring(1),
  format: (v: string) => `#${v}`,
  required: true,
  type: 'color',
  className: 'mb-0',
  size: 'sm',
} as const;

function ColorRow({ label, bg, fg }: { fg: string; bg: string; label: ReactNode }): ReactElement {
  return (
    <Stack direction="horizontal" align="center" mb="1" pb="1" borderBottom="subtle">
      <Box width="1/3">{label}</Box>
      <Box width="1/3">
        <TextField name={bg} {...colorProps} />
      </Box>
      <Box width="1/3">
        <TextField name={fg} {...colorProps} />
      </Box>
    </Stack>
  );
}

const validateZoom = composeValidators(required, (v: string): string | undefined => {
  if (v) {
    const intVal = parseInt(v, 10);
    if (intVal < 80 || intVal > 150) {
      return 'Has to be between 80 and 150';
    }
  }
  return undefined;
});

function Form(): ReactElement {
  return (
    <div>
      <Stack space="0" my="3">
        <Stack
          direction="horizontal"
          align="center"
          mb="1"
          pb="1"
          borderBottom="subtle"
          textSize="xs"
          color="secondary"
          textTransform="uppercase"
        >
          <Box width="1/3"></Box>
          <Box width="1/3">Background</Box>
          <Box width="1/3">Foreground</Box>
        </Stack>
        <ColorRow label="Header" bg="xlsx_theme.accent_bg" fg="xlsx_theme.accent_fg" />
        <ColorRow label="Heading 1" bg="xlsx_theme.h1_bg" fg="xlsx_theme.h1_fg" />
        <ColorRow label="Heading 2" bg="xlsx_theme.h2_bg" fg="xlsx_theme.h2_fg" />
        <ColorRow label="Position" bg="xlsx_theme.position_bg" fg="xlsx_theme.position_fg" />
      </Stack>

      <div className="form-row">
        <CheckField
          name="xlsx_theme.show_grid_lines"
          label="Show grid lines"
          className="col"
          variant="switch"
          size="sm"
        />
        <CheckField name="xlsx_theme.bordered" label="Bordered" className="col" variant="switch" size="sm" />

        <SelectField
          name="xlsx_theme.font_name"
          label="Font name"
          className="col"
          options={fontOptions}
          simple
          required
        />
        <IntField name="xlsx_theme.zoom_scale" label="Zoom scale" className="col" validate={validateZoom} required />
      </div>
    </div>
  );
}

export function XLSXTheme(): ReactElement {
  const theme = useFieldValue<XLSXThemeConfig>('xlsx_theme', { allowNull: false });
  return (
    <Box>
      <legend>XLSX theme</legend>
      <Form />
      <legend>Preview</legend>
      <Preview theme={theme} />
    </Box>
  );
}
